import { type ComponentProps, useEffect, useState } from 'react';

import { CheckoutModalContainer } from '#components/molecules/checkout/CheckoutModalContainer/CheckoutModalContainer.js';
import { Checkout } from '#components/organisms/checkout/Checkout.js';
import { useShopProvider } from '#provider/useShopProvider.js';
import { useShopRoutes } from '#routes/useShopRoutes.js';
import { usePlatformService } from 'core';
import { useNavigate, useParams } from 'react-router-dom';
import { Stack } from 'ui';

import { NativeCheckoutModal } from './Checkout.native';
import { CheckoutConfirmationModal } from './CheckoutConfirmationModal';
import { NPOCheckoutConfirmationModal } from './NPOCheckoutConfirmationModal';

/**
 * Guards against checkoutId being undefined for the checkout content to render correctly
 * if checkoutId is undefined, the checkout content will not render and the onCheckoutLoadingError function will be called
 */
const CheckoutContentGuard = ({
  checkoutId,
  onCheckoutLoadingError,
  onCheckoutSuccess,
  containerProps,
}: ComponentProps<typeof Checkout>) => {
  useEffect(() => {
    if (!checkoutId) {
      onCheckoutLoadingError();
    }
  }, [checkoutId, onCheckoutLoadingError]);

  return (
    <Checkout
      checkoutId={checkoutId}
      onCheckoutLoadingError={onCheckoutLoadingError}
      onCheckoutSuccess={onCheckoutSuccess}
      containerProps={containerProps}
    />
  );
};

const CheckoutPage = () => {
  const { checkoutId } = useParams();

  if (!checkoutId) return null;

  return (
    <Stack gap="xl">
      <Checkout
        checkoutId={checkoutId}
        onCheckoutLoadingError={() => null}
        onCheckoutSuccess={() => null}
      />
    </Stack>
  );
};

const PostCheckoutModals = ({
  isNPOStore,
  isConfirmationModalOpen,
  closeCheckoutRoute,
  checkoutId,
}: {
  isNPOStore?: boolean;
  isConfirmationModalOpen: boolean;
  closeCheckoutRoute: () => void;
  checkoutId?: string;
}) => {
  return (
    <>
      {isNPOStore ? (
        <NPOCheckoutConfirmationModal
          opened={isConfirmationModalOpen}
          onClose={closeCheckoutRoute}
          cartId={checkoutId}
        />
      ) : (
        <CheckoutConfirmationModal
          opened={isConfirmationModalOpen}
          onClose={closeCheckoutRoute}
          checkoutId={checkoutId}
        />
      )}
    </>
  );
};

/**
 * CheckoutModal is a modal that displays the checkout page.
 * It is used in the web version of the app.
 */

const CheckoutModal = () => {
  const platform = usePlatformService();
  const { checkoutId } = useParams();
  const { closeCheckoutRoute } = useShopRoutes();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const { isNPOStore } = useShopProvider();
  const navigate = useNavigate();

  if (platform.isNative) {
    return <NativeCheckoutModal />;
  }

  return (
    <CheckoutModalContainer
      opened={!isConfirmationModalOpen}
      onClose={closeCheckoutRoute}
      modalSlot={
        <PostCheckoutModals
          isNPOStore={isNPOStore}
          isConfirmationModalOpen={isConfirmationModalOpen}
          closeCheckoutRoute={closeCheckoutRoute}
          checkoutId={checkoutId}
        />
      }
    >
      <CheckoutContentGuard
        checkoutId={checkoutId}
        onCheckoutSuccess={() => {
          if (isNPOStore) {
            // Route is handled in memberhub.js/src/App.tsx
            navigate(`/shop/post-checkout?checkout_id=${checkoutId}`);
          } else {
            setIsConfirmationModalOpen(true);
          }
        }}
        onCheckoutLoadingError={closeCheckoutRoute}
      />
    </CheckoutModalContainer>
  );
};

export { CheckoutModal, CheckoutPage };
